// Storage worker for iOS compatibility using createSyncAccessHandle
class StorageWorker {
  constructor() {
    this.opfsRoot = null
  }

  async init() {
    if (!this.opfsRoot) {
      this.opfsRoot = await navigator.storage.getDirectory()
    }
  }

  async storeFile(subjectId, fileName, fileData) {
    await this.init()
    
    const subjectsDir = await this.opfsRoot.getDirectoryHandle('subjects', { create: true })
    const subjectDir = await subjectsDir.getDirectoryHandle(subjectId, { create: true })
    const fileHandle = await subjectDir.getFileHandle(fileName, { create: true })
    
    // Use createSyncAccessHandle for better iOS compatibility
    const syncAccessHandle = await fileHandle.createSyncAccessHandle()
    
    try {
      // Convert ArrayBuffer or Blob to Uint8Array if needed
      let data
      if (fileData instanceof ArrayBuffer) {
        data = new Uint8Array(fileData)
      } else if (fileData instanceof Blob) {
        data = new Uint8Array(await fileData.arrayBuffer())
      } else {
        data = new Uint8Array(fileData)
      }
      
      // Write data using sync access handle
      syncAccessHandle.write(data, { at: 0 })
      syncAccessHandle.flush()
      
      return {
        success: true,
        name: fileName,
        size: data.length
      }
    } finally {
      syncAccessHandle.close()
    }
  }

  async getFile(subjectId, fileName) {
    await this.init()
    
    try {
      const subjectsDir = await this.opfsRoot.getDirectoryHandle('subjects')
      const subjectDir = await subjectsDir.getDirectoryHandle(subjectId)
      const fileHandle = await subjectDir.getFileHandle(fileName)
      
      const file = await fileHandle.getFile()
      return {
        success: true,
        file: file,
        name: fileName,
        size: file.size,
        type: file.type
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      }
    }
  }

  async listFiles(subjectId) {
    await this.init()
    
    try {
      const subjectsDir = await this.opfsRoot.getDirectoryHandle('subjects')
      const subjectDir = await subjectsDir.getDirectoryHandle(subjectId)
      
      const files = []
      for await (const [name, handle] of subjectDir.entries()) {
        if (handle.kind === 'file') {
          const file = await handle.getFile()
          files.push({
            name,
            size: file.size,
            type: file.type
          })
        }
      }
      
      return {
        success: true,
        files
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      }
    }
  }

  async deleteSubject(subjectId) {
    await this.init()
    
    try {
      const subjectsDir = await this.opfsRoot.getDirectoryHandle('subjects')
      await subjectsDir.removeEntry(subjectId, { recursive: true })
      
      return {
        success: true
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      }
    }
  }
}

// Worker message handler
const storageWorker = new StorageWorker()

self.onmessage = async function(e) {
  const { id, method, params } = e.data
  
  try {
    let result
    
    switch (method) {
      case 'storeFile':
        result = await storageWorker.storeFile(params.subjectId, params.fileName, params.fileData)
        break
      case 'getFile':
        result = await storageWorker.getFile(params.subjectId, params.fileName)
        break
      case 'listFiles':
        result = await storageWorker.listFiles(params.subjectId)
        break
      case 'deleteSubject':
        result = await storageWorker.deleteSubject(params.subjectId)
        break
      default:
        result = {
          success: false,
          error: `Unknown method: ${method}`
        }
    }
    
    self.postMessage({
      id,
      success: true,
      result
    })
  } catch (error) {
    self.postMessage({
      id,
      success: false,
      error: error.message
    })
  }
}